import api from 'src/store/api'
import http from 'src/store/http'
import logger from 'src/plugins/logger'
import server from 'src/store/server'
import transform from 'src/utils/transform'

import Organization from 'src/internal/store/utils/organization'
import User from 'src/internal/store/utils/user'

import { EmailAuthProvider } from 'firebase/auth'
import { clearAllListeners } from 'src/services/listenerRegistry'
import searchService from 'src/services/searchService'
import {
  fireanalytics,
  fireauth,
  FIREBASE_DOMAIN_NAME,
  FIREBASE_DOMAIN_NAME_FOR_ADOPS_TENANT,
  firecollection,
  recoverAppCheck,
  setTenantIdOnFireauth,
  waitForFirebaseReady,
} from 'src/store/firebase'
import { storeGoogleAccessToken, clearGoogleAccessToken } from '@/utils/googleAuth'
import { parseDemoModeUrlParam } from '@/utils/urlParams'

import actionNames from './actionNames'
import { copyObject, createObject } from '@/utils/objects'

const getEnterpriseDoc = (enterpriseId) => {
  const collectionName = 'enterprise'
  return firecollection.get(collectionName, enterpriseId)
}

const getDomainDoc = (domain) => {
  const collectionName = 'domain'
  return firecollection.get(collectionName, domain)
}

const getDomainDocs = () => server.listDomains()

const getDomainByTenantId = (tenantId) => server.getDomainByTenantId(tenantId)

const getProviderDoc = (providerId) => {
  const collectionName = 'provider'
  return firecollection.get(collectionName, providerId)
}

const createProvider = (domainName, providerId, providerData) => server.createProvider(domainName, providerId, providerData)

const listProviders = (domainName, providerType) => server.listProviders(domainName, providerType)

const listTenants = () => server.listTenants()

const getTenant = (tenantId) => server.getTenant(tenantId)

/**
 * Normalized auth error codes that indicate the current session should be
 * treated as terminal, even if Firebase sign-out reports an error.
 * Keeping this list explicit makes future policy updates predictable.
 * @type {ReadonlyArray<string>}
 */
const SIGN_OUT_LISTENER_CLEANUP_CODES = Object.freeze([
  'auth/id-token-expired',
  'auth/invalid-user-token',
  'auth/user-disabled',
  'auth/user-not-found',
  'auth/user-signed-out',
  'auth/user-token-expired',
])

/**
 * Normalized error keys that should also trigger listener cleanup.
 * @type {ReadonlyArray<string>}
 */
const SIGN_OUT_LISTENER_CLEANUP_KEYS = Object.freeze([
  'AUTH_ERROR',
])

/**
 * Extract the normalized sign-out error code from either wrapped or raw shape.
 * @param {Object|null|undefined} signOutError
 * @returns {string}
 */
const getSignOutErrorCode = (signOutError) => String(
  signOutError?.error?.code || signOutError?.code || '',
)

/**
 * Extract the normalized sign-out error key from either wrapped or raw shape.
 * @param {Object|null|undefined} signOutError
 * @returns {string}
 */
const getSignOutErrorKey = (signOutError) => String(
  signOutError?.error?.key || signOutError?.key || '',
)

/**
 * Return a stable source label for listener cleanup analytics.
 * @param {Object|null|undefined} signOutError
 * @returns {'success_signout'|'terminal_signout_error'|'none'}
 */
const getSignOutCleanupSource = (signOutError) => {
  if (!signOutError) {
    return 'success_signout'
  }

  const code = getSignOutErrorCode(signOutError)
  const key = getSignOutErrorKey(signOutError)
  const isTerminal = SIGN_OUT_LISTENER_CLEANUP_CODES.includes(code)
    || SIGN_OUT_LISTENER_CLEANUP_KEYS.includes(key)

  return isTerminal ? 'terminal_signout_error' : 'none'
}

/**
 * Ordered policy predicates that decide whether listener cleanup should run.
 * The first matching predicate wins.
 * @type {ReadonlyArray<Function>}
 */
const SIGN_OUT_LISTENER_CLEANUP_CONDITIONS = Object.freeze([
  ({ signOutError }) => !signOutError,
  ({ code }) => SIGN_OUT_LISTENER_CLEANUP_CODES.includes(code),
  ({ key }) => SIGN_OUT_LISTENER_CLEANUP_KEYS.includes(key),
])

/**
 * Decide whether to clear listener registry after a sign-out attempt.
 *
 * Why this policy exists:
 * - Always clear on successful sign-out.
 * - Also clear on explicit terminal auth failures where the session can no
 *   longer be trusted and stale listeners should be torn down proactively.
 * - Keep listeners intact for transient failures (for example network outages)
 *   so active sessions do not lose real-time updates unnecessarily.
 *
 * @param {Object} options
 * @param {Object|null|undefined} options.signOutError - Normalized error.
 * @returns {boolean}
 */
export const shouldClearListenersAfterSignOut = ({ signOutError }) => {
  const evaluation = {
    signOutError,
    code: getSignOutErrorCode(signOutError),
    key: getSignOutErrorKey(signOutError),
  }

  return SIGN_OUT_LISTENER_CLEANUP_CONDITIONS.some((condition) => condition(evaluation))
}

const actions = {
  async watch({ commit, dispatch }, router) {
    // NOTE: getRedirectResult() is NOT called here because it's incompatible with COOP headers.
    // With Cross-Origin-Opener-Policy: same-origin-allow-popups enabled (required for security),
    // calling getRedirectResult() throws permission-denied errors even when no redirect is pending.
    // We rely solely on the popup flow (signInWithPopup) which works with COOP headers.
    // If popups are blocked, users will see an error message to enable popups for authentication.

    const autoAuth = async (user) => dispatch('autoAuth', { router, user })

    const errorCallback = (error) => {
      const { message = 'Authentication Watch Error' } = error

      fireanalytics.event('exception', {
        category: 'Auth',
        description: 'Auto Auth',
        label: `Auto Auth Failed with Error "${error.message}"`,
      })

      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message, status: 'error' }, { root: true })
    }

    return fireauth.onAuthStateChanged(autoAuth, errorCallback)
  },

  async getUserByEmail(context, email) {
    return firecollection.auth(email)
  },

  /**
   * @function handleAuthError
   * @desc Centralized authentication error handler.
   *
   * ERROR DISPLAY STRATEGY (Consolidated Path):
   * - Authentication errors are displayed via the AuthError dialog ONLY
   * - The SIGN_IN_FAILED mutation triggers the dialog visibility
   * - Snackbar notifications are NOT used for auth errors to avoid duplicate messages
   *
   * If you need to show a snackbar for a specific auth-related use case,
   * call the snackbar mutation explicitly AFTER this function, not within it.
   *
   * Error Flow:
   * 1. Firebase Auth error → handleAuthError or handleSignInError
   * 2. Error normalized and mapped to user-friendly message
   * 3. SIGN_IN_FAILED mutation stores error in state.didSignInFail
   * 4. AuthError.vue component watches didSignInFail and shows dialog
   *
   * @param {Object} context - Vuex context
   * @param {Object} givenError - Firebase auth error or custom error object
   * @returns {Object} Normalized error object
   */
  handleAuthError({ commit }, givenError) {
    const messageMap = {
      'auth/email-already-in-use': 'Account Already Exists',
      'auth/invalid-email': 'Invalid Email',
      'auth/user-disabled': 'User Disabled',
      'auth/user-not-found': 'User Not Found',
      'auth/wrong-password': 'Incorrect Password',
    }

    // Construct the error object.
    const { code, message: givenMessage } = givenError || {}
    const rawMessage = givenMessage || 'Auth Error'
    const message = messageMap[code] || rawMessage.replace('Firebase: ', 'Auth ')
    const normalError = givenError || { code: '400', layer: 'app', message }

    normalError.error = true

    // NOTE: Snackbar removed here to avoid duplicate error displays.
    // The AuthError dialog will show this error instead via SIGN_IN_FAILED mutation.
    // If you need a snackbar for a specific use case, call it explicitly after this function.

    // Commit the error to show the AuthError dialog
    commit(actionNames.SIGN_IN_FAILED, normalError)

    // Return the error for further handling.
    return normalError
  },

  /**
   * @function handleSignInError
   * @desc Handles errors that occur during sign in.
   * @param {Object} context The Vuex context object.
   * @param {Error} givenError The error that occurred during sign in, typically a Firebase error.
   * @return {Object} The error as a property of an object.
   */
  async handleSignInError({ dispatch }, givenError) {
    // handleAuthError now commits SIGN_IN_FAILED internally, so we don't need to do it here
    const error = await dispatch('handleAuthError', givenError)

    // Return the error for further handling.
    return error
  },

  async getEnterpriseFromFirestore(context, enterpriseId) {
    if (!enterpriseId) {
      return { error: true, message: 'Enterprise ID is required.', status: 404 }
    }

    const enterpriseDoc = await getEnterpriseDoc(enterpriseId)

    // Check if the document was created.
    if (!enterpriseDoc) {
      // Return an error because the [DEFAULT] enterprise was not found.
      return { error: true, message: 'Enterprise not found.', status: 404 }
    }

    // Check for errors.
    if (enterpriseDoc.error) {
      return enterpriseDoc
    }

    return enterpriseDoc
  },

  async getEnterprises() {
    return firecollection.collection('enterprise')
  },

  async getDomains() {
    return firecollection.collection('domain')
  },

  async getEnterpriseByDomain(context, domainName) {
    // Get the enterprise document that has the given domain name in its domains array.
    // where() expects an array of [field, operator, value] clause tuples - a single
    // un-wrapped clause here made it iterate the 3 elements as if each were its own
    // clause, destructuring each string by character (field='d', operator='o', ...),
    // which Firestore rejected with "Unknown field filter op."
    const whereClauses = firecollection.where([['domains', 'array-contains', domainName]])

    return firecollection.query('enterprise', whereClauses)
  },

  async getTenant(context, tenantId) {
    const getResult = await getTenant(tenantId)
    const { error, message } = getResult || {}

    if (error) {
      return { error, message: message || 'Error getting tenant.' }
    }

    return message
  },

  async listTenants() {
    const listResult = await listTenants()
    const { error, message } = listResult || {}

    if (error) {
      return { error, message: message || 'Error listing tenants.' }
    }

    return message
  },

  async getDomainDocFromFirestore(context, domainName) {
    // Before this function runs, the domainName is updated if the user is internal.
    const isInternalDomainUser = domainName === FIREBASE_DOMAIN_NAME_FOR_ADOPS_TENANT

    // Determine whether the domain configurations used are the default.
    const isDefaultDomainConfig = FIREBASE_DOMAIN_NAME === '[DEFAULT]'

    // Get the domainName value appropriate to the env configs and user.
    const getInternalDomainConfig = () => isInternalDomainUser && domainName
    const getDefaultDomainConfig = () => !isInternalDomainUser && isDefaultDomainConfig && FIREBASE_DOMAIN_NAME
    const getCustomDomainConfig = () => !isDefaultDomainConfig && domainName

    // Determine the user's domain.
    const docId = getInternalDomainConfig() || getDefaultDomainConfig() || getCustomDomainConfig()

    // Get the domain from the Firestore collection of domains.
    // If the domain document is found, return it.
    // On permission error, attempt App Check throttle recovery and retry once.
    try {
      const domainDoc = await getDomainDoc(docId)

      // firecollection.get returns { error } on permission failures
      if (domainDoc && domainDoc.error) {
        const errorMsg = domainDoc.error.message || ''
        const isPermissionError = /missing or insufficient permissions|access denied/i.test(errorMsg)

        if (isPermissionError) {
          logger.warn('[Auth] Domain read blocked - attempting App Check recovery')
          const recovered = await recoverAppCheck()
          if (recovered) {
            logger.info('[Auth] App Check recovered - retrying domain read')
            return getDomainDoc(docId)
          }
        }

        return domainDoc
      }

      return domainDoc
    } catch (error) {
      return { error }
    }
  },

  async getDomainByTenantId(context, tenantId) {
    const getResult = await getDomainByTenantId(tenantId)
    const { error, message } = getResult || {}

    if (error) {
      return { error, message: message || 'Error getting domain by tenant ID.' }
    }

    return message
  },

  async getDomainDocs() {
    // Get the domain documents from the server.
    const result = await getDomainDocs()
    const { error, message } = result || {}

    if (!result || error) {
      return { error, message: message || 'Error getting domain documents.' }
    }

    return message
  },

  async getProviderFromFirestore(context, { domainDoc, providerId }) {
    // Get the primary provider from the domain document.
    const providers = Array.isArray(domainDoc.providers) ? domainDoc.providers : []
    const primaryProvider = providers.find((provider) => provider.providerId === providerId) || providers[0]
    const primaryProviderId = primaryProvider.providerId

    // If the provider document is not found, return the error for handling.
    return getProviderDoc(primaryProviderId).catch((error) => ({ error }))
  },

  async createProvider(context, { domainName, providerId, providerData }) {
    // Create the provider document in Firestore.
    const create = await createProvider(domainName, providerId, providerData)
    const { error, message } = create || {}

    if (!create) {
      return { error: true, message: 'Unknown error creating provider.' }
    }

    if (error) {
      return error
    }

    // Log the creation to Google Analytics.
    fireanalytics.event('goal_completion', {
      category: 'Provider',
      description: 'Create Provider',
      label: `Create Provider "${providerData.providerId}" Succeeded`,
    })

    return message
  },

  async listProviders(context, domainName) {
    // Set the filterType. Must be 'saml' or 'oidc'.
    const providerType = 'saml'

    // Get the provider documents from the server.
    const result = await listProviders(domainName, providerType)
    const { error, message } = result || {}

    if (!result) {
      return { error, message: message || 'Unknown error getting provider documents.' }
    }

    if (error) {
      return error
    }

    // Return the array of provider documents.
    return message
  },

  async disableProvider(context, { domainName, providerData, providerId }) {
    // Disable the provider in Firebase Auth and Firestore.
    const disableResult = await server.disableProvider(domainName, providerId, providerData)
    const { error, message } = disableResult || {}

    if (!disableResult) {
      // Log the error to Google Analytics.
      fireanalytics.event('exception', {
        category: 'Provider',
        description: 'Disable Provider',
        label: `Disable Provider "${providerId}" Failed with Unknown Error`,
      })

      return { error: true, message: 'Unknown error disabling provider.' }
    }

    if (error) {
      // Log the error to Google Analytics.
      fireanalytics.event('exception', {
        category: 'Provider',
        description: 'Disable Provider',
        label: `Disable Provider "${providerId}" Failed with Error "${message}"`,
      })

      return { error, message: message || 'Unknown error disabling provider.' }
    }

    // Log the deactivation to Google Analytics.
    fireanalytics.event('goal_completion', {
      category: 'Provider',
      description: 'Deactivate Provider',
      label: `Deactivate Provider "${providerId}" Succeeded`,
    })

    return message
  },

  async enableProvider(context, { domainName, providerData, providerId }) {
    // Enable the provider in Firebase Auth and Firestore.
    const enableResult = await server.enableProvider(domainName, providerId, providerData)
    const { error, message } = enableResult || {}

    if (!enableResult) {
      // Log the error to Google Analytics.
      fireanalytics.event('exception', {
        category: 'Provider',
        description: 'Enable Provider',
        label: `Enable Provider "${providerId}" Failed with Unknown Error`,
      })

      return { error: true, message: 'Unknown error enabling provider.' }
    }

    if (error) {
      // Log the error to Google Analytics.
      fireanalytics.event('exception', {
        category: 'Provider',
        description: 'Enable Provider',
        label: `Enable Provider "${providerId}" Failed with Error "${message}"`,
      })

      return { error, message: message || 'Unknown error enabling provider.' }
    }

    // Log the activation to Google Analytics.
    fireanalytics.event('goal_completion', {
      category: 'Provider',
      description: 'Activate Provider',
      label: `Activate Provider "${providerId}" Succeeded`,
    })

    return message
  },

  async updateProvider(context, { domainName, providerId, providerData }) {
    const update = await server.updateProvider(domainName, providerId, providerData)
    const { error, message } = update || {}

    if (!update) {
      // Log the error to Google Analytics.
      fireanalytics.event('exception', {
        category: 'Provider',
        description: 'Update Provider',
        label: `Update Provider "${providerId}" Failed with Unknown Error`,
      })

      return { error: true, message: 'Unknown error updating provider.' }
    }

    if (error) {
      // Log the error to Google Analytics.
      fireanalytics.event('exception', {
        category: 'Provider',
        description: 'Update Provider',
        label: `Update Provider "${providerId}" Failed with Error "${message}"`,
      })

      return update
    }

    // Log the update to Google Analytics.
    fireanalytics.event('goal_completion', {
      category: 'Provider',
      description: 'Update Provider',
      label: `Update Provider "${providerId}" Succeeded`,
    })

    return message
  },

  setUserEmail({ commit }, email) {
    commit('userEmail', email)
  },

  setUserPassword({ commit }, password) {
    commit('userPassword', password)
  },

  // Firebase Auth will fail to update the password if the user is not signed in.
  async updatePassword({ commit }, { email, password, tenantId, url }) {
    // Set tenantId before calling Firebase Auth methods.
    commit('tenantId', tenantId)

    if (tenantId) {
      setTenantIdOnFireauth(tenantId)
    }

    // Construct the email link credential from the current URL.
    const freshCredential = await EmailAuthProvider.credentialWithLink(email, url).catch((error) => {
      const { code, message } = error
      return { code, error: true, message, status: 'error' }
    })

    if (!freshCredential || freshCredential.error) {
      return freshCredential || { error: true, message: 'Credential Error', status: 'error' }
    }

    return fireauth.updatePassword(email, password).then(() => {
      fireanalytics.event('goal_completion', {
        category: 'Auth',
        description: 'Update Password',
        label: 'Update Password Succeeded',
      })

      return { error: false, message: 'Password Updated' }
    }).catch((error) => {
      const { code, message } = error
      return { code, error: true, message, status: 'error' }
    })
  },

  sendEmailVerification(context, { url }) {
    // eslint-disable-next-line arrow-body-style
    return fireauth.sendEmailVerification(url).then(() => {
      return { error: false, message: 'Verification Email Sent' }
    }).catch((error) => {
      const { code, message } = error
      return { code, error: true, message, status: 'error' }
    })
  },

  async sendSignInLinkToEmail({ commit }, { email, tenantId, url }) {
    commit(actionNames.SIGN_IN_REQUESTED, email)

    // Save the email to local storage for use in the sign-in callback.
    localStorage.setItem('emailForSignIn', email)

    // Set tenantId before calling Firebase Auth methods.
    commit('tenantId', tenantId)

    if (tenantId) {
      setTenantIdOnFireauth(tenantId)
    }

    return fireauth.sendSignInLinkToEmail(email, url).then(() => {
      // Log to Google Analytics.
      fireanalytics.event('goal_completion', {
        category: 'Auth',
        description: 'Password Reset Email',
        label: 'Password Reset Email Sent',
      })

      return { error: false, message: 'Password Reset Email Sent' }
    }).catch((error) => {
      const { code, message } = error
      return { code, error: true, message, status: 'error' }
    })
  },

  reauthenticateWithCredential({ commit }, credential) {
    // Re-authenticate the user with this credential.
    return fireauth.reauthenticateWithCredential(credential).then((payload) => {
      commit(actionNames.SIGN_IN_SUCCEEDED, payload)

      fireanalytics.event('goal_completion', {
        category: 'Auth',
        description: 'Reauthentication',
        label: 'Reauthentication Succeeded',
      })

      return { error: false, message: 'Reauthentication Succeeded' }
    }).catch((error) => {
      const { code, message } = error
      return { code, error: true, message, status: 'error' }
    })
  },

  reauthenticateWithCredentialFromUrl({ dispatch }, { email, url }) {
    // Construct the email link credential from the current URL.
    const credential = EmailAuthProvider.credentialWithLink(email, url)

    return dispatch('reauthenticateWithCredential', credential)
  },

  linkWithCredential({ commit }, credential) {
    return fireauth.linkWithCredential(credential).then((payload) => {
      commit(actionNames.SIGN_IN_SUCCEEDED, payload)

      fireanalytics.event('goal_completion', {
        category: 'Auth',
        description: 'Account Link',
        label: 'Account Linked',
      })

      return { error: false, message: 'Account Linked' }
    }).catch((error) => {
      const { code, message } = error
      return { code, error: true, message, status: 'error' }
    })
  },

  linkWithCredentialFromUrl({ dispatch }, { email, url }) {
    // Construct the email link credential from the current URL.
    const credential = EmailAuthProvider.credentialWithLink(email, url)

    return dispatch('linkWithCredential', credential)
  },

  signInWithCredential({ commit }, credential) {
    return fireauth.signInWithCredential(credential).then((payload) => {
      commit(actionNames.SIGN_IN_SUCCEEDED, payload)

      fireanalytics.event('goal_completion', {
        category: 'Auth',
        description: 'Sign In',
        label: 'Sign In with Credential Succeeded',
      })

      return { error: false, message: 'Sign In Succeeded' }
    }).catch((error) => {
      const { code, message } = error

      return { code, error: true, message, status: 'error' }
    })
  },

  signInWithLink({ commit }, { email, tenantId, url }) {
    const isSignInWithEmailLink = fireauth.isSignInWithEmailLink(url)

    if (!isSignInWithEmailLink) {
      return { error: true, message: 'Invalid Sign-In Link', status: 'error' }
    }

    commit(actionNames.SIGN_IN_REQUESTED, email)

    // Set tenantId before calling Firebase Auth methods.
    commit('tenantId', tenantId)

    if (tenantId) {
      setTenantIdOnFireauth(tenantId)
    }

    return fireauth.signInWithEmailLink(email, url).then((payload) => {
      commit(actionNames.SIGN_IN_SUCCEEDED, payload)

      // Remove the email from local storage after sign-in.
      localStorage.removeItem('emailForSignIn')

      // You can access the new user by importing getAdditionalUserInfo
      // and calling it with result:
      // getAdditionalUserInfo(result)
      // You can access the user's profile via:
      // getAdditionalUserInfo(result)?.profile
      // You can check if the user is new or existing:
      // getAdditionalUserInfo(result)?.isNewUser

      return { error: false, message: 'Sign In Succeeded' }
    }).catch((error) => {
      const { code, message } = error
      return { code, error: true, message, status: 'error' }
    })
  },

  createUserWithEmailAndPassword({ commit }, { email, password, tenantId }) {
    commit(actionNames.SIGN_IN_REQUESTED, email)

    // Set tenantId before calling Firebase Auth methods.
    commit('tenantId', tenantId)

    if (tenantId) {
      setTenantIdOnFireauth(tenantId)
    }

    return fireauth.createUserWithEmailAndPassword(email, password).then((payload) => {
      commit(actionNames.SIGN_IN_SUCCEEDED, payload)

      fireanalytics.event('goal_completion', {
        category: 'Auth',
        description: 'Create User',
        label: `Create User Succeeded for Tenant "${tenantId}"`,
      })

      return { error: false, message: 'Account Registration Succeeded' }
    }).catch((error) => {
      const { code, message } = error
      const errorToHandle = { code, error: true, message, status: 'error' }

      if (error.code === 'auth/email-already-in-use') {
        errorToHandle.message = 'This account is already registered. Sign in to continue.'
      }

      fireanalytics.event('exception', {
        category: 'Auth',
        description: 'Create User',
        label: `Create User Failed with Error "${message}"`,
      })

      return errorToHandle
    })
  },

  async signInWithEmailAndPassword({ commit }, { email, password, provider, tenantId }) {
    commit(actionNames.SIGN_IN_REQUESTED, email)

    // Set tenantId before calling Firebase Auth methods.
    commit('tenantId', tenantId)

    if (tenantId) {
      setTenantIdOnFireauth(tenantId)
    }

    return fireauth.signInWithPassword(email, password).then((payload) => {
      // Normalize payload for the sign-in success handlers.
      // eslint-disable-next-line no-underscore-dangle
      payload.credential = payload.credential || payload._tokenResponse?.idToken

      // Commit the sign-in success.
      commit(actionNames.SIGN_IN_SUCCEEDED, payload)

      // Log the sign-in success to Google Analytics.
      fireanalytics.event('goal_completion', {
        category: 'Auth',
        description: 'Sign In',
        label: `Sign In Succeeded with Password Provider (ID ${provider.providerId}) for Tenant "${tenantId}"`,
      })

      // Return the success message for further handling.
      return { error: false, message: 'Sign In Succeeded' }
    }).catch((error) => {
      const { code, message } = error
      const errorToHandle = { code, error: true, message, status: 'error' }

      fireanalytics.event('exception', {
        category: 'Auth',
        description: 'Sign In',
        label: `Sign In with Password Provider (ID ${provider.providerId}) Failed with Error "${message}"`,
      })

      return errorToHandle
    })
  },

  async signIn({ commit, dispatch }, signInData) {
    const { email, isInternalDomain, password, provider, tenantId } = signInData

    commit(actionNames.SIGN_IN_REQUESTED, email)

    // Determine whether the user is known.
    const isUserKnowable = typeof email === 'string' && email.includes('@')

    if (!isUserKnowable) {
      const message = 'Known Work Email Required'
      return { code: '400', layer: 'app', message, status: 'error' }
    }

    /**
     * @todo Determine whether the user exists.
     *
     * @security Email Enumeration Attacks
     * In conjunction with blocking functions, this check mitigates the risk
     * of a real user failing sign-in by using an email that is not known.
     * However, this check may open the door to an email enumeration attack.
     * To mitigate this risk, the error message should be generic and not
     * reveal whether the email is known or not.
     */
    // const doesUserExist = fireauth.getFirebaseAuthUser(user.email)
    //
    // if (!doesUserExist) {
    //   const message = 'Known Work Email Required'
    //   const error = { code: '403', layer: 'app', message }
    //
    //   commit('tools/snackbar', true, { root: true })
    //   commit('tools/snack', { message, status: 'error' }, { root: true })
    //
    //   return commit(actionNames.SIGN_IN_FAILED, error)
    // }

    if (!provider) {
      return { code: '404', error: true, message: 'Provider Not Found', status: 'error' }
    }

    if (provider.error) {
      return provider.error
    }

    // Set tenantId before calling Firebase Auth methods.
    commit('tenantId', tenantId)

    // Set the tenantId on the user before calling signInWithProvider or signInWithEmailAndPassword.
    if (tenantId) {
      setTenantIdOnFireauth(tenantId)
    }

    const callSuccess = (payload, action) => {
      // Store Google OAuth access token for Sheets API access (for @adops.com users)
      // Note: Only google.com provider returns OAuth token.
      // SAML (saml.google-workspace) does NOT return credential - users must use "Grant Access" button.
      // Try credential.accessToken first, then _tokenResponse.oauthAccessToken (for redirect results)
      // eslint-disable-next-line no-underscore-dangle
      const hasOAuthToken = payload.credential?.accessToken || payload._tokenResponse?.oauthAccessToken

      if (provider.providerId === 'google.com' && hasOAuthToken) {
        storeGoogleAccessToken(payload)
      }

      fireanalytics.event('goal_completion', {
        category: 'Auth',
        description: 'Sign In',
        label: `Sign In Succeeded with ${provider.providerName} Provider (ID ${provider.providerId}) for Tenant "${tenantId}"`,
      })

      return dispatch('setupSignedInUser', { action, payload })
    }

    const callError = (error) => {
      const { code } = error || {}
      let { message } = error || {}

      // Improve error messages for common auth issues
      if (code === 'auth/network-request-failed') {
        message = 'Network Unavailable - Please check your internet connection'
      } else if (code === 'auth/popup-blocked' || message?.includes('Popup Blocked')) {
        message = 'Popup Blocked. Please allow popups for this site in your browser settings to sign in.'
      } else if (code === 'auth/popup-closed-by-user') {
        message = 'Sign-in Cancelled - Popup was closed before completing authentication'
      } else if (code === 'auth/cancelled-popup-request') {
        message = 'Sign-in Cancelled - Another sign-in popup is already open'
      } else if (message && message.includes('appCheck')) {
        // Handle App Check errors (throttling, token issues, etc.)
        message = 'Authentication Temporarily Unavailable - Our security system is rate-limiting requests. Please try again in a few minutes or contact support if this persists.'
      } else if (!message || message === 'Network Error' || message.includes('ERR_FAILED')) {
        // Generic network errors that don't have a specific code
        message = 'Connection Failed - Unable to reach authentication service. Please check your internet connection and try again.'
      }

      const errorToHandle = { code, error: true, message, status: 'error' }

      fireanalytics.event('exception', {
        category: 'Auth',
        description: 'Sign In',
        label: `Sign In with ${provider.providerName} (ID ${provider.providerId}) Failed with Error "${message}"`,
      })

      return errorToHandle
    }

    // If the provider is password, sign in with email and password.
    if (provider.providerId === 'password') {
      return dispatch('signInWithEmailAndPassword', { email, password, provider, tenantId }).then((payload) => {
        const action = 'Sign In with Password Provider'

        return callSuccess(payload, action)
      }).catch(callError)
    }

    // If the provider is not password, sign in with the provider.
    const payload = await fireauth.signInWithProvider(provider, isInternalDomain).catch(callError)

    // If there was an error during sign-in, return the error for handling.
    if (!payload || payload.error) {
      return payload
    }

    const action = `Sign In with ${provider.providerName} Provider`

    return callSuccess(payload, action)
  },

  async getPersonDocuments(context, catcher) {
    return firecollection.collection('person', catcher)
  },
  async getAliasOptions({ getters }, docId) {
    const { isUserInternalDev } = getters
    const persons = await firecollection.collection('person', docId)

    const getNonDevs = () => persons.filter((person) => {
      const { access } = person
      const { level, permissions, role } = access || {}

      const isPersonInternalAdmin = role === 'internal' && level === 'admin'
      const hasInternalDevPermissions = isPersonInternalAdmin && permissions.dev === true

      return !hasInternalDevPermissions
    })

    const getExternals = () => persons.filter((person) => {
      const { access } = person
      const { role } = access || {}

      return role === 'external'
    })

    return isUserInternalDev ? getNonDevs() : getExternals()
  },

  async getMessages(context, email) {
    return firecollection.get('message', email)
  },

  async upsertFirestore({ getters }, { collection, docId, doc }) {
    const options = { merge: true }
    const savedRecord = await firecollection.get(collection, docId)

    const { selfEmail } = getters
    const currentUser = fireauth.getCurrentUser()
    const committerUid = currentUser?.uid

    // Add the committer information to the document.
    doc.committerEmail = selfEmail
    doc.committerUid = committerUid

    if (savedRecord) {
      return firecollection.set(collection, savedRecord.docId, doc, { merge: true })
    }

    return firecollection.set(collection, docId, doc, options)
  },
  async updateUserSettings({ dispatch }, { doc, docId }) {
    const collection = 'person'

    return dispatch('upsertFirestore', { collection, docId, doc })
  },
  async upsertUser({ dispatch }, personUser) {
    const collection = 'person'
    const doc = User.mapUser(personUser)
    const docId = personUser.email

    return dispatch('upsertFirestore', { collection, docId, doc })
  },
  async deleteUser({ dispatch }, personUser) {
    if (!personUser.access) {
      personUser.access = { active: false }
    } else {
      personUser.access.active = false
    }

    const collection = 'person'
    const doc = User.mapUser(personUser)
    const docId = personUser.email

    return dispatch('upsertFirestore', { collection, docId, doc })
  },

  handleUpdateToUserSettings({ commit, dispatch, getters, state }, { changes, fnSuccess }) {
    const { user } = state
    const { inAliasMode } = getters
    const currentSettings = inAliasMode ? getters.settings : getters.selfSettings
    const docId = inAliasMode && !changes.alias ? user.aliasId : user.docId

    const settings = Object.keys(changes).reduce((newObject, key) => {
      newObject[key] = changes[key]
      return newObject
    }, currentSettings)
    const doc = { settings }
    const dataToSubmit = { doc, docId }
    const fnSuccessDefault = () => {
      const message = 'Saved'
      const status = 'success'

      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message, status }, { root: true })
    }

    return dispatch('updateUserSettings', dataToSubmit)
      .then(fnSuccess || fnSuccessDefault)
      .catch((error) => {
        const { message = 'Unknown Error ' } = error
        const status = 'error'

        commit('tools/snackbar', true, { root: true })
        commit('tools/snack', { message, status }, { root: true })
      })
  },

  async updateAliasMode({ commit, dispatch, getters }, alias) {
    commit(actionNames.SET_ALIAS_MODE_REQUESTED)

    const isSettingAliasModeOn = (alias && alias.active && 'On') || 'Off'

    // SECURITY: Capture alias mode activation details for audit logging
    // This is a privileged operation that allows internal users to impersonate others
    const { selfEmail } = getters
    const aliasEmail = alias?.email || 'none'
    const timestamp = new Date().toISOString()
    const auditEvent = {
      actor: selfEmail,
      target: aliasEmail,
      action: isSettingAliasModeOn === 'On' ? 'ALIAS_ACTIVATED' : 'ALIAS_DEACTIVATED',
      metadata: { timestamp },
    }

    // Log security-relevant alias mode change to console for server-side log aggregation
    // eslint-disable-next-line no-console
    console.info('[SECURITY_AUDIT] Alias mode change:', JSON.stringify(auditEvent))

    // Send audit event to backend for IP capture (fire and forget - don't block the flow)
    api.logSecurityAudit(auditEvent).catch((error) => {
      // eslint-disable-next-line no-console
      console.warn('[SECURITY_AUDIT] Failed to send audit to backend:', error)
    })

    fireanalytics.event('goal_initialization', {
      category: 'Alias',
      description: `Set Alias Mode ${isSettingAliasModeOn}`,
      label: `Set Alias Mode ${isSettingAliasModeOn} Requested`,
    })

    const hasRightsToUpdate = getters.hasAliasPermission

    const catcher = (err) => {
      const error = http.catch(err)
      const errorMessage = error.message || 'Unknown Error'

      // Update state with error.
      commit(actionNames.SET_ALIAS_MODE_FAILED, error)

      // Log error to analytics.
      fireanalytics.event('exception', {
        category: 'Alias',
        description: `Set Alias Mode ${isSettingAliasModeOn}`,
        label: `Set Alias Mode ${isSettingAliasModeOn} Failed with Error "${error.message}"`,
      })

      // SECURITY: Log failed alias attempt
      // eslint-disable-next-line no-console
      console.warn('[SECURITY_AUDIT] Alias mode change FAILED:', JSON.stringify({ ...auditEvent, error: errorMessage }))

      // Send failed attempt to backend for IP capture
      api.logSecurityAudit({ ...auditEvent, action: 'ALIAS_ATTEMPT_FAILED', metadata: { ...auditEvent.metadata, error: errorMessage } }).catch(() => {})

      // Inform user of error.
      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message: errorMessage, status: 'error' }, { root: true })

      return error
    }

    // If user is not authorized to update alias mode, return error.
    if (!hasRightsToUpdate) {
      // SECURITY: Log unauthorized alias attempt
      // eslint-disable-next-line no-console
      console.warn('[SECURITY_AUDIT] Unauthorized alias attempt:', JSON.stringify({ ...auditEvent, error: 'FORBIDDEN' }))

      // Send unauthorized attempt to backend for IP capture
      api.logSecurityAudit({ ...auditEvent, action: 'ALIAS_ATTEMPT_UNAUTHORIZED' }).catch(() => {})

      const error = { code: '403', layer: 'app', message: 'Forbidden' }
      return catcher({ error })
    }

    // Update the alias settings on the user record.
    const fnSuccess = () => {
      const message = 'Updated Alias Settings'
      const status = 'success'

      // Inform user of success.
      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message, status }, { root: true })

      // Update state with success.
      commit(actionNames.SET_ALIAS_MODE_SUCCEEDED, alias)

      // SECURITY: Log successful alias mode change
      // eslint-disable-next-line no-console
      console.info('[SECURITY_AUDIT] Alias mode change SUCCESS:', JSON.stringify(auditEvent))

      // Log success to analytics.
      fireanalytics.event('goal_completion', {
        category: 'Alias',
        description: `Set Alias Mode ${isSettingAliasModeOn}`,
        label: `Set Alias Mode ${isSettingAliasModeOn} Succeeded`,
      })

      // Reload page to update view.
      window.location = '/'
    }

    const changes = { alias }

    return dispatch('handleUpdateToUserSettings', { changes, fnSuccess })
  },

  async updateDarkMode({ commit, dispatch, getters }, themes) {
    commit(actionNames.SET_DARK_MODE_REQUESTED)

    const { themes: savedThemes } = getters
    const savedDarkModeSetting = savedThemes.activeTheme === 'dark'

    const labelMap = {
      undefined: 'Undefined',
      light: 'Off',
      dark: 'On',
    }
    const actionLabel = labelMap[themes.activeTheme]
    const analyticsAction = `from "${labelMap[savedDarkModeSetting]}" to "${actionLabel}"`

    fireanalytics.event('goal_initialization', {
      category: 'Dark Mode',
      description: `Set Dark Mode ${actionLabel}`,
      label: `Set Dark Mode ${analyticsAction} Requested`,
    })

    const hasRightsToUpdate = true

    const catcher = (err) => {
      const error = http.catch(err)
      const errorMessage = error.message || 'Unknown Error'

      // Update state with error.
      commit(actionNames.SET_DARK_MODE_FAILED, error)

      // Log error to analytics.
      fireanalytics.event('exception', {
        category: 'Dark Mode',
        description: `Set Dark Mode ${actionLabel}`,
        label: `Set Dark Mode ${analyticsAction} Failed with Error "${error.message}"`,
      })

      // Inform user of error.
      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message: errorMessage, status: 'error' }, { root: true })

      return error
    }

    // If user is not authorized to update dark mode, return error.
    if (!hasRightsToUpdate) {
      const error = { code: '403', layer: 'app', message: 'Forbidden' }
      return catcher({ error })
    }

    // Update the `themes` setting on the user record.
    const fnSuccess = () => {
      const message = 'Updated Dark Mode Setting'
      const status = 'success'

      // Inform user of success.
      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message, status }, { root: true })

      // Update state with success.
      commit(actionNames.SET_DARK_MODE_SUCCEEDED, themes)

      // Log success to analytics.
      fireanalytics.event('goal_completion', {
        category: 'Dark Mode',
        description: `Set Dark Mode ${actionLabel}`,
        label: `Set Dark Mode ${analyticsAction} Succeeded`,
      })

      // The view will update without a page reload.
    }

    const changes = { themes }

    return dispatch('handleUpdateToUserSettings', { changes, fnSuccess })
  },

  async setDemoMode({ commit }, newDemoMode) {
    commit('demoMode', newDemoMode)

    await api.set(newDemoMode)

    return newDemoMode
  },

  async updateDemoMode({ commit, dispatch, rootGetters }, demo) {
    commit(actionNames.SET_DEMO_MODE_REQUESTED)

    const isSettingDemoModeOn = demo === true || (typeof demo === 'object' && demo.active === true)
    const statusWord = isSettingDemoModeOn ? 'On' : 'Off'

    fireanalytics.event('goal_initialization', {
      category: 'Demo',
      description: `Set Demo Mode ${statusWord}`,
      label: `Set Demo Mode ${statusWord} Requested`,
    })

    const hasRightsToUpdate = rootGetters['auth/isDemoWriter']

    const catcher = (err) => {
      const error = http.catch(err)
      const errorMessage = error.message || 'Unknown Error'

      // Update state with error.
      commit(actionNames.SET_DEMO_MODE_FAILED, error)

      // Log error to analytics.
      fireanalytics.event('exception', {
        category: 'Demo',
        description: `Set Demo Mode ${statusWord}`,
        label: `Set Demo Mode ${statusWord} Failed with Error "${error.message}"`,
      })

      // Inform user of error.
      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message: errorMessage, status: 'error' }, { root: true })

      return error
    }

    if (!hasRightsToUpdate) {
      const error = { code: '403', layer: 'app', message: 'Forbidden' }
      return catcher({ error })
    }

    const fnSuccess = async () => {
      commit(actionNames.SET_DEMO_MODE_SUCCEEDED, demo)

      fireanalytics.event('goal_completion', {
        category: 'Demo',
        description: `Set Demo Mode ${isSettingDemoModeOn}`,
        label: `Set Demo Mode ${isSettingDemoModeOn} Succeeded`,
      })

      await dispatch('setDemoMode', demo.active)

      window.location = '/'
    }

    const changes = { demo }

    return dispatch('handleUpdateToUserSettings', { changes, fnSuccess })
  },
  /**
   * Gets a Tableau token from the server.
   * @returns {ServerResponse}
   */
  async getTableauToken() {
    return server.generate('tableau')
  },
  setEmailOnRequests(context, person) {
    if (!person) return false

    return http.setRequester(person.docId)
  },

  /**
   * Fetches collection and saves it to store by its endpointName.
   * @return items {Array} Result of requestCollection, which traces to tools/successHandler action.
   */
  async getCollectionByName({ dispatch }, { endpointName, moduleName, params = {}, reportKey, user }) {
    const requestCollection = async () => {
      const fetch = await dispatch('tools/request', { moduleName, params, reportKey, user }, { root: true })
      return dispatch('tools/render', fetch, { root: true })
    }

    const applyFilters = endpointName === moduleName
    const requestReport = { applyFilters, endpointName, moduleName, reportKey, requestCollection }

    return dispatch('tools/getCollection', requestReport, { root: true })
  },
  async getOrganizations({ dispatch }, { moduleName, user }) {
    const endpointName = 'organizations'
    const reportKey = 'organizations'
    const params = {}

    return dispatch('getCollectionByName', { endpointName, moduleName, params, reportKey, user })
  },
  /**
   * Gets persons from API, plus users from Firestore, to make PersonUsers.
   * @param {Object} context
   * @param {Object} data
   * @returns {PersonUser[]} PersonUsers
   */
  async getPersonUsers({ dispatch }, { moduleName, params, user }) {
    const endpointName = 'persons'
    const reportKey = 'people'

    return dispatch('getCollectionByName', { endpointName, moduleName, params, reportKey, user })
  },
  /**
   * @function getProjects
   * @desc Fetches projects from API and saves them to store.
   * @param {Object} context
   * @param {Object} data
   * @returns {Promise} Promise representing the API call
   *
   * @see $store.actions.tools.fetchProjects This is the underlying fetch logic.
   */
  async getProjects({ dispatch }, { moduleName, user }) {
    const endpointName = 'projects'
    const reportKey = 'projects'
    const params = {}

    return dispatch('getCollectionByName', { endpointName, moduleName, params, reportKey, user })
  },
  async getServices({ dispatch }, { moduleName, user }) {
    const endpointName = 'services'
    const reportKey = 'service'
    const params = {}

    return dispatch('getCollectionByName', { endpointName, moduleName, params, reportKey, user })
  },
  async getTimes({ dispatch }, { moduleName, user }) {
    const endpointName = 'times'
    const reportKey = 'time'
    const params = {}

    return dispatch('getCollectionByName', { endpointName, moduleName, params, reportKey, user })
  },
  async getTimeTypes({ dispatch }, { moduleName, user }) {
    const endpointName = 'timeTypes' // @todo Change 'timeTypes' endpointName to 'time-types' for consistency.
    const reportKey = 'timeType'
    const params = {}

    return dispatch('getCollectionByName', { endpointName, moduleName, params, reportKey, user })
  },

  getOrgData({ dispatch, rootGetters, state }, reportId) {
    if (reportId) {
      return rootGetters['tools/organization']
    }

    const { user } = state
    const moduleName = 'organizations'

    return dispatch('getOrganizations', { moduleName, user })
  },

  async getAllTeams({ commit }, publisher) {
    const { publisherId } = publisher
    const teamsCatcher = () => commit('GET_TEAMS_FAILED', publisher)

    return api.getTeams(publisherId).catch(teamsCatcher)
  },
  findNetworkTeams(context, { billingCode, networks }) {
    const findConfiguredTeams = (network) => {
      const { organization: networkOrg, team: networkTeams } = network
      return String(networkOrg.code) === String(billingCode) ? networkTeams : false
    }

    return networks.find(findConfiguredTeams) || []
  },
  hasAllTeamsSet(context, teams) {
    if (!Array.isArray(teams)) return false
    return !!teams.find((team) => team.id === '-1' && team.code === '-1')
  },
  async getConfiguredTeams({ dispatch }, { publisher, networks }) {
    const { billingCode, publisherId } = publisher
    const publisherNetworkData = { billingCode, networks }
    const network = await dispatch('findNetworkTeams', publisherNetworkData)
    const { team: networkTeams } = network
    const hasAllTeamsSet = await dispatch('hasAllTeamsSet', networkTeams)

    if (hasAllTeamsSet) {
      return dispatch('getAllTeams', publisher)
    }

    const teamIds = networkTeams.map((networkTeam) => networkTeam.id)
    const content = api.getTeamsByPublisherId(publisherId, teamIds)

    return { content, error: false }
  },
  async setTeamsOnPublisher({ dispatch }, requestData) {
    const { isInternalUser, publisher } = requestData
    const success = (teamsResponse) => {
      const { content: teams, error: teamError } = teamsResponse

      publisher.teams = teamError ? teamsResponse : teams

      return publisher
    }

    if (isInternalUser) {
      return dispatch('getAllTeams', publisher).then(success)
    }

    return dispatch('getConfiguredTeams', requestData).then(success)
  },
  setTeamsOnPublishers({ dispatch }, { isInternalUser, publishersResponse, user }) {
    const { network: networks = [{}] } = user
    const { content: publishers, error: publisherError } = publishersResponse

    return publisherError || Promise.all(publishers.map(async (publisher) => {
      const getPublisherWithoutTeams = () => !publisher.hasTeam && Promise.resolve(publisher)
      const getPublisherWithTeams = () => {
        const requestData = { isInternalUser, publisher, networks }
        return dispatch('setTeamsOnPublisher', requestData)
      }

      return getPublisherWithoutTeams() || getPublisherWithTeams()
    }))
  },

  async getClients({ dispatch, rootGetters }, clientId) {
    const endpointName = 'clients'
    const moduleName = 'organizations'
    const reportKey = rootGetters['tools/reportKey']
    const root = { root: true }
    const applyFilters = false
    const requestReport = { applyFilters, endpointName, reportKey }

    const handler = await dispatch('tools/requestHandler', requestReport, root)
    const { catcher, success } = handler

    const response = await api.getClients(clientId).catch(catcher)
    const { content: clients, error } = response || {}

    if (error) return catcher(response)

    const items = clients.map((client) => {
      client.text = client.clientName
      client.value = client.clientId

      return client
    })
    const responseReport = { endpointName, items, moduleName, success }

    return dispatch('tools/requestSuccess', responseReport, root)
  },

  /**
   * Gets publishers and their teams from the API.
   * @param {Object} context
   * @param {function} context.dispatch
   * @param {Object} data
   * @param {boolean} data.isInternalUser
   * @param {Object} data.user { network, role }
   * @return {Promise<ApiResponse>}
   */
  fetchPublishersByRole({ dispatch }, { isInternalUser, user }) {
    const setTeamsOnPublishers = (publishersResponse) => dispatch('setTeamsOnPublishers', { isInternalUser, publishersResponse, user })
    const makePublisherResponse = (publishers) => ({ content: publishers, totalCount: publishers.length })
    const internalRequest = () => api.getPublishers().then(setTeamsOnPublishers).then(makePublisherResponse)
    const externalRequest = () => {
      const { network: networks = [{}] } = user
      const billingCodes = networks.map((network) => {
        const { organization = {} } = network
        const { code = '' } = organization
        return code
      })

      return api.getPublishersByBillingCodes(billingCodes).then(setTeamsOnPublishers).then(makePublisherResponse)
    }

    return isInternalUser ? internalRequest() : externalRequest()
  },
  /**
   * Gets publishers and their teams from the API, then stores the results.
   * @param {Object} context
   * @param {function} context.dispatch
   * @param {object} context.rootGetters
   * @param {Object} data
   * @param {boolean} data.isInternalUser
   * @param {Object} data.user { network, role }
   * @return {Promise<Publishers|ErrorMessage>}
   */
  async getPublishers({ dispatch, rootGetters }, { isInternalUser, user }) {
    const endpointName = 'publishers'
    const moduleName = 'organizations'
    const reportKey = rootGetters['tools/reportKey']
    const root = { root: true }
    const applyFilters = false
    const requestReport = { applyFilters, endpointName, reportKey }

    const handler = await dispatch('tools/requestHandler', requestReport, root)
    const { catcher, success } = handler

    const response = await dispatch('fetchPublishersByRole', { isInternalUser, user })
    const { content: items, error } = response
    const storePublishers = () => {
      const responseReport = { endpointName, items, moduleName, success }
      return dispatch('tools/requestSuccess', responseReport, root)
    }

    return error ? catcher(error) : storePublishers()
  },
  getOrgConfig(context, catcher) {
    return firecollection.collection('organization', catcher)
  },
  getOrgConfigByBillingCode(context, { billingCodes, catcher }) {
    const promises = billingCodes.map((billingCode) => firecollection.org(billingCode, catcher))

    return Promise.all(promises)
  },
  getOrgRequestsByRole({ commit, dispatch }, user) {
    const isInternalUser = user.access?.role === 'internal'
    const { network: networks } = user
    const catcher = (err) => {
      const { message = err.error?.message } = err

      if (!err.metadata) err.metadata = { email: user.docId, message }

      const error = http.catch(err)

      const errorMessage = message || 'Error Unknown'

      // Replace error message's ephemeral snackbar with a persistent dialog.
      commit('tools/snackbar', true, { root: true })
      commit('tools/snack', { message: errorMessage, status: 'error' }, { root: true })

      return error
    }

    const getOrgConfigByBillingCode = () => {
      const billingCodes = networks.reduce((billingCodesArray, network) => {
        const { organization: { code: billingCode } } = network
        if (!billingCodesArray.includes(billingCode)) billingCodesArray.push(billingCode)
        return billingCodesArray
      }, [])
      return dispatch('getOrgConfigByBillingCode', { billingCodes, catcher })
    }

    return {
      clients: isInternalUser ? dispatch('getClients') : [],
      configs: isInternalUser ? dispatch('getOrgConfig', catcher) : getOrgConfigByBillingCode(),
      publishers: dispatch('getPublishers', { isInternalUser, user }),
    }
  },
  async fetchClientsAndPublishers({ dispatch }, user) {
    const requestsByRole = await dispatch('getOrgRequestsByRole', user)

    const { clients: clientsReq, configs, publishers: publishersReq } = requestsByRole

    return Promise.all([publishersReq, clientsReq, configs, Promise.resolve(user)])
  },
  reduceClientsAndPublishers(context, { clients, configs, publishers, user }) {
    if (user.access?.role === 'internal') {
      configs.forEach((config) => {
        config.isInternal = true
      })
    }

    const makeOrgs = Organization.reduceClientsAndPublishers.bind(Organization, configs)
    const clientsAndPublishers = []
    const labelClient = (item) => {
      item.isObjectClient = true

      return item
    }
    const labelPublisher = (item) => {
      item.isObjectPublisher = true

      return item
    }

    clientsAndPublishers.push(...clients.map(labelClient), ...publishers.map(labelPublisher))

    return clientsAndPublishers.reduce(makeOrgs, [])
  },
  async transformClientsAndPublishers({ dispatch }, data) {
    const { handler: { catcher }, response } = data
    const [publishers, clients, configs, user] = response

    if (!Array.isArray(clients)) return catcher(clients)
    if (!Array.isArray(publishers)) return catcher(publishers)

    data.items = await dispatch('reduceClientsAndPublishers', { clients, configs, publishers, user })

    return data
  },
  /**
   * Flattens organizations and their teams.
   * @param {Object} context vuexContext
   * @param {Organizations} organizations
   * @return {Promise<ErrorMessage|Teams>}
   */
  async getOrganizationsAsTeams(context, organizations) {
    const error = () => !Array.isArray(organizations) && organizations
    const success = () => organizations.reduce((orgsAndTeams, org) => {
      orgsAndTeams.push(...org.teams)

      return orgsAndTeams
    }, [])

    return error() || success()
  },

  getAuthedUserOrganizations({ dispatch, state }, moduleName) {
    const { user } = state
    return dispatch('getOrganizations', { moduleName, user })
  },

  async fetchInternalTeams() {
    return api.getInternalTeams()
  },

  async fetchPersonAndUser({ dispatch }, { catcher, params }) {
    const isReportIdNumerical = /^\d+$/.test(params.reportId)
    const isReportIdEmail = !isReportIdNumerical && /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(params.reportId)

    const isReportIdValid = isReportIdNumerical || isReportIdEmail

    if (!isReportIdValid) {
      const error = { code: 400, layer: 'app', message: 'Invalid Report ID' }
      return catcher({ error })
    }

    // Fetch the person by personId if reportId is a number.
    // Fetch the person by email if reportId is an email.
    const requestByPersonId = () => isReportIdNumerical && api.getPersonById(params.reportId).catch(catcher)
    const requestByEmail = () => isReportIdEmail && api.findPersonsByEmailContains(params.reportId).catch(catcher)

    const personsRequest = requestByPersonId() || requestByEmail()
    const personResponse = await personsRequest

    const { content } = personResponse
    const [{ personEmail }] = (Array.isArray(content) && content) || [{ personEmail: null }]

    const usersRequest = (personEmail && dispatch('getUserByEmail', personEmail).then((user) => [user])) || {
      code: 404,
      layer: 'app/data',
      message: 'User Not Found',
      metadata: { personId: params.reportId },
    }
    const teamsRequest = dispatch('fetchInternalTeams', catcher)

    // This return signature should be consistent with the one in fetchPersonsAndUsers.
    return [personsRequest, usersRequest, teamsRequest]
  },
  async fetchPersonsAndUsers({ dispatch }, { catcher, params, reportKey }) {
    const { billingCode, personEmail, personId, reportId } = params || {}
    const isPersonRoute = /person/.test(reportKey)

    const useSearchByReportId = () => reportId && typeof reportId === 'string' && reportId.length > 0

    const fetchPersonById = async () => isPersonRoute && useSearchByReportId() && dispatch('fetchPersonAndUser', { catcher, params })

    const apiRequests = {
      getAll: () => api.getPersons().catch(catcher),
      byPersonEmail: () => api.findPersonsByEmailContains(personEmail).catch(catcher),
      byPersonEmails: (personEmails) => api.findPersonsByEmails(personEmails).catch(catcher),
      byPersonId: () => api.getPersonById(personId).catch(catcher),
      byReportId: () => dispatch('fetchPersonAndUser', { catcher, params }),
    }
    const userFilter = {
      getAll: (users) => users,
      byBillingCode: (users) => users.filter((user) => {
        const { network: networks } = user
        const billingCodes = networks.map((network) => {
          const { organization } = network

          // Handle case where organization is malformed or missing.
          const assignedOrg = (typeof organization === 'object' && organization) || {}
          const { code } = assignedOrg
          const haveBillingCode = typeof code === 'string' && code.length > 0

          return haveBillingCode ? code : null
        })

        return billingCodes.indexOf(billingCode) !== -1
      }),
      byPersonEmail: (users) => users.filter((user) => {
        const { docId } = user
        return docId === personEmail || RegExp(personEmail).test(docId)
      }),
    }

    const useSearchByBillingCode = () => billingCode && typeof billingCode === 'string' && billingCode.length > 0
    const useSearchByEmail = () => personEmail && typeof personEmail === 'string' && personEmail.length > 0
    const useSearchByPersonId = () => personId && typeof personId === 'string' && personId.length > 0

    const useRequestByReportId = () => isPersonRoute && useSearchByReportId()
    const useRequestByPersonId = () => isPersonRoute && useSearchByPersonId()
    const useRequestByBillingCode = () => isPersonRoute && useSearchByBillingCode()
    const useRequestByEmail = () => isPersonRoute && useSearchByEmail()

    const sendRequestByBillingCode = useRequestByBillingCode()
    const sendRequestByEmail = useRequestByEmail()
    const sendRequestByPersonId = useRequestByPersonId()
    const sendRequestByReportId = useRequestByReportId()

    const documentRequest = dispatch('getPersonDocuments', catcher)
    const documentResponse = await documentRequest

    const requestUsersEndpoint = () => {
      const requestAll = () => userFilter.getAll(documentResponse)
      const requestByEmail = () => sendRequestByEmail && userFilter.byPersonEmail(documentResponse)
      const requestByBillingCode = () => sendRequestByBillingCode && userFilter.byBillingCode(documentResponse)

      return requestByEmail() || requestByBillingCode() || requestAll()
    }

    const usersResponse = await requestUsersEndpoint()

    const fetchPersons = async () => {
      const requestPersonsEndpoint = () => {
        const requestByPersonId = () => sendRequestByPersonId && apiRequests.byPersonId()
        const requestByReportId = () => sendRequestByReportId && apiRequests.byReportId()
        const requestByBillingCode = () => {
          if (sendRequestByBillingCode) {
            const emails = usersResponse.map((user) => user.docId)

            return apiRequests.byPersonEmails(emails)
          }

          return false
        }
        const requestByEmail = () => sendRequestByEmail && apiRequests.byPersonEmail()
        const requestAll = () => apiRequests.getAll()

        return requestByReportId() || requestByPersonId() || requestByBillingCode() || requestByEmail() || requestAll()
      }

      const filterUsersByPersons = (personsReq) => personsReq.then((apiResponse) => {
        const persons = apiResponse.content
        const personsEmails = persons.map((user) => user.personEmail)

        return usersResponse.filter((user) => personsEmails.indexOf(user.docId) !== -1)
      })

      const personsRequest = requestPersonsEndpoint().catch(catcher)
      const usersRequest = sendRequestByPersonId || sendRequestByReportId ? filterUsersByPersons(personsRequest) : usersResponse
      const teamsRequest = dispatch('fetchInternalTeams', catcher)

      // This return signature should be consistent with the one in fetchPersonAndUser.
      return [personsRequest, usersRequest, teamsRequest]
    }

    const promiseArray = await fetchPersonById() || await fetchPersons()

    return Promise.all(promiseArray)
  },
  async transformPersonUsers({ dispatch, rootGetters }, data) {
    const { endpointName, handler, response } = data
    const [personsResponse, users, internalTeamsResponse] = response
    const { catcher } = handler

    if (!personsResponse || !Array.isArray(personsResponse.content)) {
      data.response.error = personsResponse
    }

    if (!users || !Array.isArray(users)) {
      data.response.error = users
    }

    if (!internalTeamsResponse || !Array.isArray(internalTeamsResponse.content)) {
      data.response.error = internalTeamsResponse
    }

    const transformer = async (responses) => {
      const personColumnsByKey = rootGetters['tools/personColumnsByKey']
      const [personsRes, /* users */, internalTeamsRes] = responses
      const { content: persons } = personsRes
      const { content: internalTeams } = internalTeamsRes

      const organizations = await dispatch('getAuthedUserOrganizations', endpointName)
      const formatAndNormalize = (item) => transform.formatAndNormalize(item, personColumnsByKey)
      const internalTeamDefault = internalTeams.find((team) => team.teamId === -10)

      const personsByEmail = persons.reduce((personsDict, person) => {
        person.isUser = false
        person.isPerson = true

        const findManagerName = () => {
          const findManager = () => persons.find((p) => p.personId === person.managerPersonId)
          const manager = person.managerPersonId && findManager()
          return manager && `${manager.personNameFirst} ${manager.personNameLast}`
        }
        const nameManagerById = () => person.managerPersonId && `Person ID ${person.managerPersonId}`
        const nameManager = () => {
          const setManagerName = () => findManagerName() || nameManagerById() || 'None'
          return person.isInternal ? setManagerName() : 'N/A'
        }

        person.managerPersonName = nameManager()

        const findInternalTeam = () => {
          const { teamId } = person

          // eslint-disable-next-line arrow-body-style
          const findInternalTeamByTeamId = () => {
            return teamId && internalTeams.find((team) => team.teamId === teamId)
          }

          return findInternalTeamByTeamId()
        }

        if (person.isInternal) {
          person.team = findInternalTeam() || internalTeamDefault
          person.teamName = person.team?.teamName || internalTeamDefault.teamName
          person.teamOfficeCode = person.team?.teamOfficeCode || internalTeamDefault.teamOfficeCode
          person.teamOfficeName = person.team?.teamOfficeName || internalTeamDefault.teamOfficeName
        } else {
          person.team = { teamId: -1, teamName: 'N/A', teamOfficeCode: 'N/A', teamOfficeName: 'N/A' }
          person.teamName = 'N/A'
          person.teamOfficeCode = 'N/A'
          person.teamOfficeName = 'N/A'
        }

        personsDict[person.personEmail] = person

        return personsDict
      }, {})

      const personUsersByEmail = users.reduce((personUsersDict, user) => {
        const { docId: userEmail } = user
        const matchingPersonRecord = personUsersDict[userEmail]

        if (matchingPersonRecord) {
          personUsersDict[userEmail] = { ...user, ...matchingPersonRecord }
        } else {
          const defaultPerson = {
            isPerson: false,
            personId: null,
            personEmail: userEmail,
          }
          personUsersDict[userEmail] = { ...user, ...defaultPerson }
        }

        personUsersDict[userEmail].isUser = true

        return personUsersDict
      }, personsByEmail)
      const personUsers = Object.keys(personUsersByEmail).map(async (emailAddress) => {
        const personUser = personUsersByEmail[emailAddress]
        const person = User.mapPersonAndUser(personUser, organizations)
        const formatAndNormalizePerson = (viewUser, key) => formatAndNormalize(person)(key)
        return Object.keys(person).reduce(formatAndNormalizePerson, person)
      })

      return Promise.all(personUsers)
    }

    const rules = [
      () => !users && 'firebase',
      () => !personsResponse && 'api',
      () => personsResponse.error && personsResponse,
    ]
    const foundError = rules.find((fnValidate) => fnValidate(personsResponse, users))
    const catchError = () => foundError && catcher(foundError)

    data.items = catchError() || await transformer(response)
    data.success = data.handler.success
    data.excluded = []

    return data
  },

  async makeUser(context, { organizations, person, user: authedUser }) {
    const personUser = { ...authedUser, ...person }
    return User.mapPersonAndUser(personUser, organizations)
  },
  async makeSelf(context, userRecord) {
    const { docId } = userRecord
    const personResponse = await api.getPersonByEmail(docId)
    const { content, error } = personResponse

    if (error || !Array.isArray(content)) {
      return copyObject(userRecord)
    }

    const [person] = content

    return createObject(userRecord, person)
  },
  /**
   * @function setUser
   * @desc Sets the user data in the store.
   * @param {VuexContext} context - The context object provided by Vuex.
   * @param {Object} records - The data required to set the user.
   * @returns {Promise<Object<{ credential: Object, user: Object }>>}
   */
  async setUser({ commit, dispatch }, records) {
    const { analyticsGoal, authedUser, catcher, credential, email } = records

    // The API has been configured by this point, so we can use it to fetch the user's person profile.
    // If the user is aliasing another user, the email address references the aliased user's email address.
    const response = await api.getPersonByEmail(email).catch(catcher)
    const { content, error } = response

    // If the API has returned an error or the response content is not an array, we throw an error.
    if (error || !Array.isArray(content)) return catcher(response)

    // If the API has returned a valid response, we destructure the person profile from the response content array.
    // We then check whether the user is authorized to access the application.
    const [person] = content
    const isAuthorizedPerson = User.isAuthorizedPerson(person)

    if (!isAuthorizedPerson) return catcher(response)

    // Configure Firebase Auth with the user's tenant (enables multi-tenant isolation).
    // The person document contains the tenantId assigned during domain resolution.
    const { tenantId: personTenantId } = person
    if (personTenantId) {
      commit('tenantId', personTenantId)
      setTenantIdOnFireauth(personTenantId)
    }

    // Validate tenant isolation: ensure user's tenant matches the target's expected tenant.
    // This is crucial for multi-tenant security: a user from one client cannot access another client's data.
    // Validation only enforces when target has an expected tenantId (empty = [DEFAULT], allows all users).
    // Example: NFL user trying to access wondery.iadops.com is rejected with clear error message.
    if (process.env.VUE_APP_TENANT_ID && personTenantId && personTenantId !== process.env.VUE_APP_TENANT_ID) {
      const message = 'Access denied. This account is not authorized for this organization. '
                      + 'Please sign out and sign in at the correct domain.'
      const tenantError = { code: 'tenant-mismatch', layer: 'app', message }

      // Reject the session outright rather than just surfacing an error - without this,
      // the user keeps a valid Firebase ID token after the mismatch is detected, which
      // could still be used against endpoints/collections that don't re-check tenancy.
      await dispatch('signOut', 'Tenant Mismatch')

      return catcher(tenantError)
    }

    // If the user is authorized and aliasing another user, we fetch the user's actual person profile
    // This is necessary because the aliased user's profile does not contain the current user's actual permissions.
    // We use the current user's actual permissions to determine whether the user can update their own alias state.
    authedUser.self = !!authedUser.self && await dispatch('makeSelf', authedUser.self)

    // We fetch the user's organizations, which are used to determine the user's permissions and settings.
    // If the user is aliasing another user, this is the aliased user's organization data.
    const moduleName = 'auth'
    const organizations = await dispatch('getOrganizations', { moduleName, user: authedUser })

    // We make the user's profile using their user record, person profile, and relevant organization data.
    const user = await dispatch('makeUser', { organizations, person, user: authedUser })
    const payload = { credential, user }

    // We commit the user to the store, triggering a cascade of events based on the user's permissions and settings.
    commit(actionNames.SIGN_IN_SUCCEEDED, payload)

    // Apply the user's saved theme preference from Firestore immediately after sign-in.
    // Support both `settings.themes.activeTheme` and `settings.preferences.theme`.
    const savedTheme = user.settings?.themes?.activeTheme || user.settings?.preferences?.theme
    if (savedTheme === 'dark' || savedTheme === 'light') {
      dispatch('layout/setDarkMode', savedTheme === 'dark', { root: true })
    }

    // Initialize navigation and search infrastructure now that user permissions are established
    // This ensures navigation and basic search (dashboard placeholders) are ready before components mount
    const navigation = await import('src/tools/store/utils/navigation')
    const navEndpoints = navigation.default().endpoints
    await dispatch('tools/setNavigation', { navEndpoints, params: {} }, { root: true })

    // Initialize search endpoints with dashboard placeholders for instant navigation
    // Full search data loads lazily when user actually uses the search bar
    await dispatch('tools/initializeSearchEndpoints', {}, { root: true })

    fireanalytics.event('store_user', analyticsGoal)

    return payload
  },
  async makeAnalyticsUser({ rootGetters }, personUser) {
    const { network } = personUser
    const networks = Organization.flattenNetworkData(network)
    const { billingCodes, teamCodes, teamIds } = networks

    const hasPacing = await rootGetters['tools/hasPacing']
    const hasDiscrepancy = await rootGetters['tools/hasDiscrepancy']
    const hasHealthAdx = await rootGetters['tools/hasHealthAdx']
    const hasHealthNetwork = await rootGetters['tools/hasHealthNetwork']
    const hasProgrammatic = await rootGetters['tools/hasProgrammatic']
    const hasYieldPartner = await rootGetters['tools/hasYieldPartner']
    const inDemoMode = rootGetters['auth/inDemoMode']

    const {
      access,
      expires,
      isDeleted,
      managerPersonId,
      personId,
    } = personUser
    const { active, level, role } = access || {}
    const version = process.env.VUE_APP_VERSION

    return {
      active,
      billingCodes: billingCodes.join(', '),
      expires,
      hasDiscrepancy,
      hasHealthAdx,
      hasHealthNetwork,
      hasProgrammatic,
      hasYieldPartner,
      hasPacing,
      isDeleted,
      inDemoMode,
      level,
      managerPersonId,
      personId,
      role,
      teamCodes: teamCodes.join(', '),
      teamIds: teamIds.join(', '),
      version,
    }
  },
  async setSentryMonitoring(context, { analyticsUser, personId }) {
    const { billingCodes, inDemoMode, level, role, teamCodes } = analyticsUser
    const { hostname } = window.document.location
    const nodeEnv = process.env.NODE_ENV
    const project = process.env.VUE_APP_GOOGLE_PROJECT
    const version = process.env.VUE_APP_VERSION

    logger.sentry.setUser({ billingCodes, inDemoMode, level, personId, role, teamCodes })
    logger.sentry.setTags({ hostname, nodeEnv, project, version })

    return analyticsUser
  },
  setGoogleAnalytics(context, analyticsData) {
    return fireanalytics.setUser(analyticsData)
  },
  async monitorUser({ dispatch }, personUser) {
    const analyticsUser = await dispatch('makeAnalyticsUser', personUser)
    const { personId = personUser.docId } = personUser
    const analyticsData = { analyticsUser, personId }

    dispatch('setGoogleAnalytics', analyticsData)
    dispatch('setSentryMonitoring', analyticsData)

    return analyticsUser
  },
  /**
   * Sets the application data in the store.
   * @param {VuexContext} context - The context object provided by Vuex.
   * @param {Object} appData - The data required to set the user.
   * @returns {Promise<Object>} - The result of the setUser action,
   * `{ credential, user }`.
   *
   * @see $store.actions.auth.setUser for more details.
   */
  async setAppData({ dispatch }, appData) {
    const userData = await dispatch('setUser', appData)
    const { user } = userData

    if (user) {
      dispatch('monitorUser', user)
    }

    return userData
  },
  async setIdTokenOnRequests(context, fireUser) {
    const token = await fireUser.getIdToken()

    http.setAuthorizationHeader(token)

    return token
  },

  /**
   * Start proactive token refresh.
   * Sets up a listener for token changes and schedules refresh before expiration.
   * Firebase ID tokens expire after 1 hour; this refreshes 5 minutes before.
   *
   * @param {VuexContext} context - The Vuex context
   * @returns {void}
   */
  startTokenRefresh({ commit, dispatch }) {
    // Clean up any existing refresh mechanism
    dispatch('stopTokenRefresh')

    // Listen for token changes (fires on refresh and sign-out)
    const unsubscribe = fireauth.onIdTokenChanged(async (user) => {
      if (!user) {
        // User signed out - clean up
        dispatch('stopTokenRefresh')
        return
      }

      try {
        // Get the fresh token
        const token = await user.getIdToken()
        http.setAuthorizationHeader(token)
        commit('idToken', token)

        // Parse token to get expiration time
        const tokenParts = token.split('.')
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]))
          const expiresAt = payload.exp * 1000 // Convert to milliseconds
          commit('tokenExpiresAt', expiresAt)

          // Schedule proactive refresh 5 minutes before expiration
          dispatch('scheduleTokenRefresh', expiresAt)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('[Token Refresh] Error handling token change:', error)
      }
    })

    commit('tokenRefreshUnsubscribe', unsubscribe)
  },

  /**
   * Schedule a proactive token refresh before expiration.
   * @param {VuexContext} context - The Vuex context
   * @param {number} expiresAt - Token expiration timestamp in milliseconds
   */
  scheduleTokenRefresh({ commit, state, dispatch }, expiresAt) {
    // Clear any existing timer
    if (state.tokenRefreshTimer) {
      clearTimeout(state.tokenRefreshTimer)
    }

    // Calculate time until refresh (5 minutes before expiration)
    const REFRESH_BUFFER_MS = 5 * 60 * 1000 // 5 minutes
    const now = Date.now()
    const timeUntilRefresh = expiresAt - now - REFRESH_BUFFER_MS

    // If token is already expired or about to expire, refresh immediately
    if (timeUntilRefresh <= 0) {
      dispatch('refreshToken')
      return
    }

    // Schedule refresh
    const timerId = setTimeout(async () => {
      await dispatch('refreshToken')
    }, timeUntilRefresh)

    commit('tokenRefreshTimer', timerId)

    // Log for monitoring (in development)
    if (process.env.NODE_ENV !== 'production') {
      const refreshIn = Math.round(timeUntilRefresh / 1000 / 60)
      // eslint-disable-next-line no-console
      console.log(`[Token Refresh] Scheduled in ${refreshIn} minutes`)
    }
  },

  /**
   * Force refresh the Firebase ID token.
   * @param {VuexContext} context - The Vuex context
   * @returns {Promise<string|null>} The new token or null if failed
   */
  async refreshToken({ commit }) {
    try {
      const token = await fireauth.forceRefreshToken()
      if (token) {
        http.setAuthorizationHeader(token)
        commit('idToken', token)

        // Parse and store new expiration
        const tokenParts = token.split('.')
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]))
          const expiresAt = payload.exp * 1000
          commit('tokenExpiresAt', expiresAt)
        }

        // eslint-disable-next-line no-console
        console.log('[Token Refresh] Token refreshed successfully')
        return token
      }
      return null
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[Token Refresh] Failed to refresh token:', error)
      return null
    }
  },

  /**
   * Stop token refresh mechanism.
   * Cleans up timer and unsubscribes from token change listener.
   * @param {VuexContext} context - The Vuex context
   */
  stopTokenRefresh({ commit, state }) {
    // Clear the refresh timer
    if (state.tokenRefreshTimer) {
      clearTimeout(state.tokenRefreshTimer)
      commit('tokenRefreshTimer', null)
    }

    // Unsubscribe from token changes
    if (state.tokenRefreshUnsubscribe) {
      state.tokenRefreshUnsubscribe()
      commit('tokenRefreshUnsubscribe', null)
    }

    // Clear expiration tracking
    commit('tokenExpiresAt', null)
  },

  async storeUser({ commit, dispatch }, data) {
    const { analyticsGoal, catcher, firestoreUser, googleProfile, userData } = data
    const { credential, user: fireUser } = userData

    fireanalytics.event('update_user', {
      category: analyticsGoal.category,
      description: analyticsGoal.description,
      label: 'Store User Requested',
    })

    // A valid `?demo=` query param overrides the saved user setting for the
    // current page load only. It never writes to local storage or Firestore.
    const demoModeFromUrl = parseDemoModeUrlParam()
    const inDemoMode = demoModeFromUrl === null
      ? User.inDemoMode(firestoreUser)
      : demoModeFromUrl

    // Set demo mode based on user record.
    await dispatch('setDemoMode', inDemoMode)

    // Configure HTTP requests for authorization.

    // Set the user's email on all API requests.
    await dispatch('setEmailOnRequests', firestoreUser).catch(catcher)

    // Set the Firebase ID token (JWT) on all API requests.
    const token = await dispatch('setIdTokenOnRequests', credential || fireUser).catch(catcher)

    // Store the Firebase ID token (JWT) for backend API authorization
    commit('idToken', token)

    // Store the tenant ID for easy reference.
    commit('tenantId', fireUser.tenantId)

    // Store the OAuth credential from the sign-in process.
    commit('credential', credential)

    const fireProperties = {
      accessToken: fireUser.accessToken,
      displayName: fireUser.displayName,
      email: fireUser.email,
      emailVerified: fireUser.emailVerified,
      isAnonymous: fireUser.isAnonymous,
      metadata: fireUser.metadata,
      phoneNumber: fireUser.phoneNumber,
      photoURL: fireUser.photoURL,
      providerData: fireUser.providerData,
      providerId: fireUser.providerId,
      tenantId: fireUser.tenantId,
      uid: fireUser.uid,
    }

    const authedUser = { ...firestoreUser, ...googleProfile, ...fireProperties }
    const appData = { analyticsGoal, authedUser, catcher, credential, email: googleProfile.email }

    // Start proactive token refresh after user is fully set up
    dispatch('startTokenRefresh')

    return dispatch('setAppData', appData)
  },
  /**
   * @function getUserRecords
   * @desc Gets the user's Firestore record and Google profile.
   * @param {Object} context
   * @param {Object} data
   * @param {Function} data.catcher Sync error handler.
   */
  async getUserRecords(context, { catcher, email }) {
    return firecollection.auth(email, catcher)
  },
  async setupSignedInUser({ commit, dispatch }, { action, payload }) {
    const analyticsEvent = 'Sign In'

    fireanalytics.event('goal_initialization', {
      category: 'Auth',
      description: analyticsEvent,
      label: `${analyticsEvent} Requested via ${action}`,
    })

    const catcher = (err) => {
      const error = http.catch(err)

      commit(actionNames.SIGN_IN_FAILED, error)

      fireanalytics.event('exception', {
        category: 'Auth',
        description: analyticsEvent,
        label: `${analyticsEvent} via ${action} Failed with Error "${error.message}"`,
      })

      return error
    }

    if (!payload) {
      // No payload - this shouldn't happen with normal flow
      // Exit. The catcher will handle it.
      return catcher({ error: false, message: 'Sign in' })
    }

    if (!payload.user) {
      // No user in payload - this shouldn't happen now that autoAuth checks haveUser first
      // Exit. The catcher will handle it.
      return catcher({ error: false, message: 'User not found' })
    }

    const fireUser = payload.user

    if (!fireUser.emailVerified) {
      // Exit. The catcher will handle it.
      return catcher({ error: { code: '403', layer: 'app', message: 'Email Not Verified' } })
    }

    const firebaseEmail = fireUser.email

    if (!firebaseEmail) {
      // Exit. The catcher will handle it.
      return catcher({ error: { code: '403', layer: 'app', message: 'Email Not Found in User Record' } })
    }

    const email = firebaseEmail.toLowerCase()
    const googleProfile = fireUser.providerData.find((profile) => profile.providerId === 'google.com')

    const firestorePersonDoc = await dispatch('getUserRecords', { catcher, email })

    if (firestorePersonDoc && firestorePersonDoc.error) {
      const firestoreCode = firestorePersonDoc.code || firestorePersonDoc.error?.code || 'unknown'
      logger.sentry.setTags({
        authUserRecordLookup: 'firestore-error',
        authUserRecordLookupCode: String(firestoreCode),
      })

      // Preserve real Firestore auth/permission errors instead of mapping
      // them to "User Record Not Found".
      return firestorePersonDoc
    }

    const foundPersonDoc = !!firestorePersonDoc && typeof firestorePersonDoc === 'object'

    if (!foundPersonDoc) {
      logger.sentry.setTags({
        authUserRecordLookup: 'not-found',
      })

      // Exit. The catcher will handle it.
      return catcher({ error: { code: '404', layer: 'app', message: 'User Record Not Found' } })
    }

    const firestoreUser = { ...firestorePersonDoc }

    if (!firestoreUser || Object.keys(firestoreUser).length === 0) {
      // Exit. The catcher will handle it.
      return catcher({ error: { code: '403', layer: 'app', message: 'Corrupted User Record Found' } })
    }

    const errors = User.authorizeUser(firestoreUser)

    if (errors.length > 0) {
      const metadata = { email: fireUser.email, errors }

      const error = { code: '403', layer: 'app', message: 'Access Denied', metadata }

      return catcher({ error })
    }

    const aliasId = User.aliasId(firestoreUser)
    const setAlias = async () => {
      const aliasUser = await firecollection.auth(aliasId, catcher)
      const self = copyObject(firestoreUser)

      fireUser.aliasId = aliasId
      firestoreUser.aliasId = aliasId

      fireUser.self = self
      firestoreUser.self = self

      fireUser.access = aliasUser.access
      fireUser.network = aliasUser.network
      fireUser.settings = aliasUser.settings

      firestoreUser.access = aliasUser.access
      firestoreUser.network = aliasUser.network
      firestoreUser.settings = aliasUser.settings
      firestoreUser.tenantId = aliasUser.tenantId

      // To emulate users before activating them, we ignore alias.access.active.
      fireUser.access.active = true
      firestoreUser.access.active = true

      fireanalytics.event('goal_progress', {
        category: 'Auth',
        description: 'Sign In',
        label: `Set Alias Mode to ${analyticsEvent} via ${action} Succeeded`,
      })

      // The docId/email is untouched because calls to the API require it.
      return { user: fireUser }
    }

    // If aliasId is present, we set the alias user data.
    // Otherwise, we use the original payload to define userData.
    const userData = (aliasId && await setAlias()) || payload

    const analyticsGoal = {
      category: 'Auth',
      description: 'Sign In',
      label: `Store User to Sign In via ${action} Succeeded`,
    }

    // Output is the result of `setUser`: { credential, user }
    const storedData = dispatch('storeUser', { analyticsGoal, catcher, firestoreUser, googleProfile, userData })

    if (storedData && storedData.user) {
      fireanalytics.event('goal_completion', {
        category: 'Auth',
        description: 'Sign In',
        label: `Sign In via ${action} Succeeded`,
      })
    }

    return storedData
  },
  async signOut({ commit, dispatch }, action = 'Unknown') {
    // Stop token refresh before signing out
    dispatch('stopTokenRefresh')

    commit(actionNames.SIGN_OUT_REQUESTED)

    fireanalytics.event('goal_initialization', {
      category: 'Auth',
      description: 'Sign Out Requested',
      label: `Sign Out Requested via ${action}`,
    })

    const catcher = (err) => {
      // Firebase signOut() sometimes rejects with null - handle gracefully
      // If there's no real error, treat sign-out as successful
      if (!err) {
        return null
      }

      const error = http.catch(err)

      commit(actionNames.SIGN_OUT_FAILED, error)

      fireanalytics.event('exception', {
        category: 'Auth',
        description: 'Sign Out Failed',
        label: `Sign Out via ${action} Failed with Error "${error.message}"`,
      })

      return error
    }

    const signOutError = await fireauth.signOut().catch(catcher)

    const shouldClearListeners = shouldClearListenersAfterSignOut({ signOutError })
    const cleanupSource = getSignOutCleanupSource(signOutError)

    fireanalytics.event('goal_progress', {
      category: 'Auth',
      description: 'Snapshot Listener Cleanup Decision',
      label: `Sign Out Cleanup via ${action} Source=${cleanupSource} Enabled=${shouldClearListeners}`,
    })

    if (shouldClearListeners) {
      clearAllListeners()
    }

    // Only commit success if there was no real error
    if (signOutError) {
      return signOutError
    }

    commit(actionNames.SIGN_OUT_SUCCEEDED)

    // Clear search cache to prevent data leakage to the next user
    searchService.clearOnSignOut()

    // Clear Google OAuth token on sign-out
    clearGoogleAccessToken()

    fireanalytics.event('goal_completion', {
      category: 'Auth',
      description: 'Sign Out Succeeded',
      label: `Sign Out via ${action} Succeeded`,
    })

    http.clearAuthToken()

    return true
  },
  async autoAuth({ commit, dispatch }, { router, user: fireUser }) {
    const haveUser = !!fireUser && typeof fireUser === 'object'

    const { currentRoute } = router

    const { meta: { requiresAuth } } = currentRoute

    if (requiresAuth === false) {
      // Exit. The user is not required to be authenticated.
      return true
    }

    const isSignOutRoute = currentRoute.name === 'sign-out'
    const signOut = (action) => dispatch('signOut', action)

    if (isSignOutRoute) {
      fireanalytics.event('goal_initialization', {
        category: 'Auth',
        description: 'Auto Auth',
        label: 'Attempt to Sign Out via Sign Out Route Requested',
      })

      // After signing out, exit. Do not auto-authenticate on the sign-out route.
      return signOut('Sign Out Route')
    }

    if (currentRoute.name === 'sign-in') {
      // Tracking only - does not force a sign-out. This used to sign out
      // any user found here on every auth state change, including the
      // moment a fresh sign-in completes on this exact route (it's where
      // the "/sign-in" link on Welcome.vue points), which immediately
      // undid a successful login. A route literally named "sign-in" never
      // existed before that link needed somewhere to go. The E2E bypass
      // that used to gate the old signOut() call (window.__E2E_CUSTOM_AUTH__,
      // still set by tests/e2e/playwright/fixtures/auth.js) is no longer
      // needed for the same reason - nothing here signs anyone out anymore.
      fireanalytics.event('goal_initialization', {
        category: 'Auth',
        description: 'Auto Auth',
        label: 'Attempt to Sign In via Sign In Route Requested',
      })

      commit(actionNames.AUTO_AUTH_COMPLETE)

      // Exit. Do not auto-authenticate on the sign-in route.
      return true
    }

    // If there's no user, let the app show the sign-in overlay (no error dialog needed)
    if (!haveUser) {
      commit(actionNames.AUTO_AUTH_COMPLETE)
      // Sign-in is shown via showSignIn getter in Layout.vue, not a route
      return { error: false, message: 'No active session' }
    }

    // Wait for Firebase readiness before protected-route sign-in.
    // This reduces startup races where auth state arrives before App Check settles.
    const readiness = await waitForFirebaseReady({
      expectedEmail: fireUser.email,
      authTimeoutMs: 10000,
      appCheckTimeoutMs: 5000,
    })

    const {
      authUser: settledAuthUser,
      appCheckReady,
      authMatchesExpected,
    } = readiness || {}
    const signInUser = settledAuthUser || fireUser

    logger.sentry.setTags({
      authBootstrapStage: 'auto-auth-ready-check',
      authBootstrapAppCheckReady: String(!!appCheckReady),
      authBootstrapAuthMatchesExpected: String(!!authMatchesExpected),
    })

    logger.sentry.addBreadcrumb({
      category: 'auth.bootstrap',
      level: 'info',
      message: 'autoAuth readiness evaluated before setupSignedInUser',
      data: {
        routeName: currentRoute?.name || 'unknown',
        appCheckReady: !!appCheckReady,
        authMatchesExpected: !!authMatchesExpected,
        hasSettledAuthUser: !!settledAuthUser,
      },
    })

    if (!appCheckReady) {
      logger.warn('[Auth] App Check token not ready before autoAuth sign-in attempt')
    }

    // Set isAutoAuthing and isAuthCheckRequested to true.
    commit(actionNames.AUTH_CHECK_REQUESTED)

    // Log the auto-authentication attempt to Google Analytics.
    fireanalytics.event('goal_initialization', {
      category: 'Auth',
      description: 'Auto Auth',
      label: 'Attempt to Sign In via Page Load Requested',
    })

    // Attempt to sign in the user.
    const signInResult = await dispatch('setupSignedInUser', {
      action: 'Page Load',
      payload: { user: signInUser },
    }).catch((err) => {
      commit(actionNames.AUTH_CHECK_FAILED, err)

      fireanalytics.event('exception', {
        category: 'Auth',
        description: 'Auto Auth',
        label: `Attempt to Sign In via Page Load Failed with Error "${err.message}"`,
      })

      return err
    })

    // Check if the user is signed in.
    const isSignedIn = signInResult && !signInResult.error

    // If the user is signed in, log the success to Google Analytics.
    if (isSignedIn) {
      fireanalytics.event('goal_completion', {
        category: 'Auth',
        description: 'Auto Auth',
        label: 'Attempt to Sign In via Page Load Succeeded',
      })
    } else if (signInResult) {
      // If the user is not signed in, log the failure to Google Analytics.
      fireanalytics.event('goal_completion', {
        category: 'Auth',
        description: 'Auto Auth',
        label: `Attempt to Sign In via Page Load Failed with Error "${signInResult.message}"`,
      })
    } else {
      // If the user is not signed in, log the failure to Google Analytics.
      fireanalytics.event('goal_completion', {
        category: 'Auth',
        description: 'Auto Auth',
        label: 'Attempt to Sign In via Page Load Failed with Empty Result',
      })
    }

    // Set isAutoAuthing and isAuthCheckRequested to false.
    commit(actionNames.AUTO_AUTH_COMPLETE)

    return signInResult
  },
  isShowingHelp(context, payload) {
    context.commit(actionNames.SHOW_HELP, payload)
  },
  getHelp(context) {
    context.commit(actionNames.SHOW_HELP, true)
  },
  hideHelp(context) {
    context.commit(actionNames.SHOW_HELP, false)
  },
}

export default actions
